/**
 * DraftStep registry — single source of truth for per-step metadata.
 *
 * **Track 2 Phase 1 deliverable** (per `docs/plans/2026-05-08_track2_drafting_unification.md`).
 *
 * ## Why this exists
 *
 * Adding a new draft primitive currently requires editing **5 different
 * switch statements** (and 1 negative-list helper) scattered across 3
 * files:
 *
 *   1. `buildDraftPrompt`               — draftingHelpers.ts:327       (210 cases)
 *   2. `buildDraftPreviewEntity`        — draftingHelpers.ts:1309      (67 cases)
 *   3. `referencePointForDraftTextInput`— draftSharedHelpers.ts:317    (45 cases)
 *   4. `advanceDraftSessionWithPoint`   — draftStateMachine.ts:146     (59 cases)
 *   5. `advanceDraftSessionWithText`    — draftStateMachine.ts:1313    (~53 if-chains)
 *   6. `draftStepAcceptsPointInput`     — draftSharedHelpers.ts:250    (negative list)
 *
 * Forget any one — silent no-op in production. This is how DIMENSION
 * text input ended up unimplemented (no `dimension-*` branch in #5).
 *
 * After Phase 3-5 of Track 2, all 5 sites become table-driven:
 *
 * ```ts
 * function buildDraftPrompt(session) {
 *   const d = getDraftStepDescriptor(session.step);
 *   return t(d.promptKey, d.promptArgs?.(session));
 * }
 * ```
 *
 * ## Phase 1 contract (THIS file)
 *
 * **No behavior change.** Phase 1 only declares the registry. Existing
 * switches in draftingHelpers.ts / draftStateMachine.ts / draftSharedHelpers.ts
 * stay intact. Phase 3-5 will progressively replace them.
 *
 * ## Source of truth
 *
 * The Record<DraftStep, DraftStepDescriptor> shape forces compile-time
 * exhaustiveness — adding a new variant to `DraftStep` in `draftTypes.ts`
 * without a registry entry is a TS error.
 *
 * ## CLAUDE.md fidelity
 *
 * Per ПРИОРИТЕТ #1 (нет упрощений) — every one of 223 steps is
 * declared explicitly. No defaults, no wildcards. Maintenance cost is
 * paid up front so future authors get compiler-enforced completeness.
 */

import type { MessageKey } from "../../i18n/messages";
import type { DraftCommandId, DraftSession, DraftStep } from "../draftTypes";

/**
 * Coarse grouping by primitive family. Used by Phase 5 dispatcher to
 * route advanceDraftSession*WithPoint to the correct sub-machine
 * (dimensions / blockAuthoring / modify) without per-step matching.
 */
export type DraftStepCategory =
  | "basic-2d"
  | "polyline"
  | "text"
  | "block-authoring"
  | "array"
  | "hatch"
  | "dimension"
  | "mleader"
  | "revcloud"
  | "wipeout"
  | "spline"
  | "viewport"
  | "modify"
  | "geom-constraint"
  | "block-param"
  | "block-action";

/**
 * Where the rubber-band reference point comes from when the user types
 * a numeric / coordinate input at this step.
 *
 *  - "last":   `session.points.at(-1)` (relative to most recent click)
 *  - "first":  `session.points[0]`     (relative to first click)
 *  - "second": `session.points[1]`     (relative to second click)
 *  - "none":   no rubber band, text input is content / scalar / mode
 *
 * Mirrors the four return paths of `referencePointForDraftTextInput`
 * in draftSharedHelpers.ts:317.
 */
export type TextInputReference = "last" | "first" | "second" | "none";

export interface DraftStepDescriptor {
  /** Step id — same string used in `DraftStep` union. */
  readonly step: DraftStep;
  /** Owning command (e.g. "LINE", "DIMALIGNED", "MOVE"). */
  readonly command: DraftCommandId;
  /** Coarse category — used by Phase 5 sub-machine dispatch. */
  readonly category: DraftStepCategory;
  /**
   * i18n key passed to `t()` to produce the prompt-line text.
   *
   * Phase 1 placeholder note: 13 steps that lack a dedicated prompt key
   * in the existing switch (the `*-ready` transient states + a few
   * geom-constraint / mtext-height edges) currently fall through to
   * `t("prompt.idle", {command})`. Registry entries for these are
   * marked with the `// TBD Phase 3` comment and use `prompt.idle`. The
   * Phase 3 PR will introduce dedicated keys (e.g. `prompt.circle.ready`)
   * + i18n translations for both RU and EN.
   */
  readonly promptKey: MessageKey;
  /**
   * Optional resolver that picks a different `promptKey` based on
   * dynamic session state (mode flags, point count). When defined, its
   * return value supersedes `promptKey` for the current call.
   *
   * Used by:
   *  - `spline-fit-next` — switches between `prompt.spline.fit_next`
   *    and `prompt.spline.fit_next_or_finish` based on
   *    `session.points.length >= 2`.
   *  - `copy-base-point`  — switches between
   *    `prompt.copy.base_or_displacement` and `prompt.copy.displacement`
   *    based on `session.options.copyDisplacementMode === "true"`.
   *
   * For all other steps, the static `promptKey` is sufficient.
   */
  readonly resolvePromptKey?: (session: DraftSession) => MessageKey;
  /**
   * Optional substitution argument provider for the prompt.
   *
   * Most steps don't need substitutions. Donut / polygon / bedit /
   * insert / bclose use `{last}`, `{name}`, `{tag}`, `{default}`
   * placeholders driven from `session.options`.
   */
  readonly promptArgs?: (session: DraftSession) => Record<string, string>;
  /**
   * Whether this step accepts a coordinate input (click or typed
   * "x,y" / "@dx,dy" / "@d<θ"). Mirrors the negation in
   * `draftStepAcceptsPointInput` (draftSharedHelpers.ts:250) — when this
   * is `false`, the step expects a scalar / mode keyword / text body.
   */
  readonly acceptsPointInput: boolean;
  /** See {@link TextInputReference}. */
  readonly textInputReference: TextInputReference;
  /**
   * Which prior point the ORTHO (ORTHOMODE / F8) 0/90° lock anchors on
   * for THIS step — i.e. `$LASTPOINT` for the rubber-band being drawn.
   *
   * Defaults to {@link textInputReference} when omitted, because for the
   * overwhelming majority of steps the ORTHO anchor and the typed-input
   * reference are the SAME prior point (the rubber band the user types a
   * relative coord against is the one ORTHO locks). Declared explicitly
   * ONLY for the handful of steps whose ORTHO anchor DIVERGES from their
   * typed-input reference:
   *
   *   - `spline-fit-next` → `"last"` (the fit curve extends from the last
   *     fit point; `draftPreviewBuilders` appends the hover to the fit
   *     points, so ORTHO must lock every point after the first onto the
   *     axis from the previous fit point — @codex P2). `textInputReference`
   *     is `"none"`.
   *   - `arc-sce-center`  → `"first"` (ARC Start-Center-End draws a radius
   *     line from the START point to the hovered center, so ORTHO locks
   *     the center onto the axis from the start). `textInputReference` `"none"`.
   *   - `rotate-ref-new-angle` → `"first"` (a CLICK measures its angle from
   *     the rotation base `points[0]`, not the reference end that the typed
   *     input references, so ORTHO locks around the base — @codex P2 round-4).
   *     `textInputReference` is `"last"`.
   *   - `revcloud-freehand-path` → `"none"` (a continuous cursor trace whose
   *     hover samples append to `points`; axis-locking would staircase the
   *     cloud AND re-fire the anchor-change refresh into a double-append, so
   *     the anchor is suppressed to null — @codex P2 round-4).
   *     `textInputReference` is `"last"`.
   *   - on-screen SCALAR / ANGLE point prompts → `"first"` (@codex P2): a step
   *     whose click measures a scalar (height / scale) or angle (rotation) from
   *     the insertion point `points[0]` declares `textInputReference: "none"`
   *     (a typed value is an absolute scalar, not a relative coord), so its
   *     ortho anchor DIVERGES to `"first"` — the drag locks onto the 0/90° axis
   *     of the insertion point. The class is `text-height`, `text-rotation`,
   *     `insert-specify-scale`, and `insert-specify-rotation`.
   *   - anchored second-point picks with a "none" typed reference → the point
   *     the handler actually measures against (@codex P2): `rotate-ref-end`
   *     → `"second"` (angle from the reference START `points[1]`) and
   *     `break-second-point` → `"first"` (span from the first break point
   *     `points[0]`). Both are anchored picks of an in-progress command whose
   *     typed reference is "none", so the ortho anchor is declared explicitly to
   *     the exact `points` index the handler reads.
   *   - box/area OPPOSITE-corner steps → `"none"` (@codex P2): a step that
   *     defines a 2D box/area by two DIAGONAL corners declares
   *     `textInputReference: "first"` ONLY so typed relative coords (`@dx,dy`
   *     from corner 1) work — but ORTHO must NOT anchor on corner 1, because
   *     locking the diagonal corner onto its 0/90° axis collapses the box to
   *     zero area (AutoCAD never ortho-constrains a two-corner box). The full
   *     class is `rectangle-second-corner`, `mtext-second-corner`,
   *     `hatch-second-corner`, `revcloud-second-corner`, `wipeout-second-corner`,
   *     `viewport-second-corner`, `vpclip-second-corner`, and `arrayrect-count`
   *     (grid opposite corner); each is pinned `orthoReference: "none"`. A
   *     registry-completeness test asserts every box/area second-corner step
   *     resolves a NULL ortho anchor.
   *   - FREE label/grip placement steps → `"none"` (@codex P2): the five BPARAM
   *     `*-label` steps (`param-linear-label`, `param-flip-label`,
   *     `param-polar-label`, `param-xy-label`, `param-rotation-label`) store the
   *     clicked point VERBATIM as the parameter's `gripPosition`. They keep
   *     `textInputReference: "second"` for typed relative coords, but ORTHO must
   *     NOT axis-lock a free label — the anchor is suppressed to null.
   *
   * A `"none"` default here is what (correctly) excludes selection-window
   * corners (`stretch-select-second` etc.): forcing the second crossing
   * corner onto the first corner's axis would collapse the window to zero
   * area — those steps are `"none"` and get NO ortho anchor.
   */
  readonly orthoReference?: TextInputReference;
  /**
   * Whether `buildDraftPreviewEntity` produces a preview EntityPreview
   * for this step. Used in Phase 4 to short-circuit dispatch when the
   * step has no rubber-band visualization (e.g. selection / dialog
   * steps).
   */
  readonly hasPreviewEntity: boolean;
}

/**
 * Helper for prompt callbacks that read `{last}` from session options.
 */
function donutInnerArgs(session: DraftSession): Record<string, string> {
  return { last: session.options.donutInnerD ?? "0.5" };
}
function donutOuterArgs(session: DraftSession): Record<string, string> {
  return { last: session.options.donutOuterD ?? "1.0" };
}
function polygonSidesArgs(session: DraftSession): Record<string, string> {
  return { last: session.options.polygonSides ?? "4" };
}
function beditConfirmArgs(session: DraftSession): Record<string, string> {
  return { name: session.options["blockName"] ?? "" };
}
function bcloseConfirmArgs(session: DraftSession): Record<string, string> {
  return { name: session.options["blockName"] ?? "" };
}
function insertCollectAttribsArgs(
  session: DraftSession,
): Record<string, string> {
  return {
    tag: session.options["attribTag"] ?? "",
    default: session.options["attribDefault"] ?? "",
  };
}

/**
 * Resolver for `spline-fit-next`: AutoCAD parity — once two fit-points
 * exist, the prompt also exposes the Enter-to-finish hint
 * (`prompt.spline.fit_next_or_finish`); below that threshold we use the
 * plain `prompt.spline.fit_next` key.
 */
function splineFitNextPromptKey(session: DraftSession): MessageKey {
  return session.points.length >= 2
    ? "prompt.spline.fit_next_or_finish"
    : "prompt.spline.fit_next";
}

/**
 * Resolver for `copy-base-point`: when the user entered Displacement
 * mode (`session.options.copyDisplacementMode === "true"`), the prompt
 * changes to the single-vector displacement prompt; otherwise the full
 * "base point or [Displacement]" prompt is shown so keyword
 * availability is discoverable. Mirrors the conditional in the legacy
 * switch.
 */
function copyBasePointPromptKey(session: DraftSession): MessageKey {
  return session.options.copyDisplacementMode === "true"
    ? "prompt.copy.displacement"
    : "prompt.copy.base_or_displacement";
}

/**
 * Args provider for the 13 `*-ready` transient states that fell through
 * to the legacy `default: return t("prompt.idle", { command: session.command })`
 * branch. Preserves byte-identical output for those steps when the
 * registry-driven `buildDraftPrompt` substitutes `{command}`.
 */
function idleCommandArgs(session: DraftSession): Record<string, string> {
  return { command: session.command };
}

/**
 * The registry. Order mirrors the source-of-truth `DraftStep` union in
 * `draftTypes.ts:104-327` for review-friendliness.
 *
 * The `Record<DraftStep, DraftStepDescriptor>` typing forces
 * exhaustiveness — a missing entry triggers a TS error.
 */
export const DRAFT_STEP_REGISTRY: Readonly<
  Record<DraftStep, DraftStepDescriptor>
> = {
  // ── LINE / POINT ────────────────────────────────────────────────────
  "line-first-point": {
    step: "line-first-point",
    command: "LINE",
    category: "basic-2d",
    promptKey: "prompt.line.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "line-next-point": {
    step: "line-next-point",
    command: "LINE",
    category: "basic-2d",
    promptKey: "prompt.line.next",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "point-location": {
    step: "point-location",
    command: "POINT",
    category: "basic-2d",
    promptKey: "prompt.point.location",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  // ── BASE / БАЗА ($INSBASE) ──────────────────────────────────────────
  "insbase-point": {
    step: "insbase-point",
    command: "BASE",
    category: "basic-2d",
    promptKey: "prompt.base.point",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── CIRCLE ──────────────────────────────────────────────────────────
  "circle-center": {
    step: "circle-center",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.center",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "circle-radius": {
    step: "circle-radius",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.radius",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "circle-diameter": {
    step: "circle-diameter",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.diameter",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: false,
  },
  "circle-ready": {
    step: "circle-ready",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated `prompt.circle.ready`
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "circle-2p-first-point": {
    step: "circle-2p-first-point",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.2p_first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "circle-2p-second-point": {
    step: "circle-2p-second-point",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.2p_second",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "circle-3p-second-point": {
    step: "circle-3p-second-point",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.3p_second",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "circle-3p-third-point": {
    step: "circle-3p-third-point",
    command: "CIRCLE",
    category: "basic-2d",
    promptKey: "prompt.circle.3p_third",
    acceptsPointInput: true,
    textInputReference: "second",
    hasPreviewEntity: true,
  },

  // ── DONUT ───────────────────────────────────────────────────────────
  "donut-inner-d": {
    step: "donut-inner-d",
    command: "DONUT",
    category: "basic-2d",
    promptKey: "prompt.donut.inner_d",
    promptArgs: donutInnerArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "donut-outer-d": {
    step: "donut-outer-d",
    command: "DONUT",
    category: "basic-2d",
    promptKey: "prompt.donut.outer_d",
    promptArgs: donutOuterArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "donut-center": {
    step: "donut-center",
    command: "DONUT",
    category: "basic-2d",
    promptKey: "prompt.donut.center",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── ARC ─────────────────────────────────────────────────────────────
  "arc-start": {
    step: "arc-start",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.arc.start",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "arc-second": {
    step: "arc-second",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.arc.second",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "arc-end": {
    step: "arc-end",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.arc.end",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "arc-ready": {
    step: "arc-ready",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "arc-sce-center": {
    step: "arc-sce-center",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.arc.sce_center",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — the preview draws a radius line START→center, so ORTHO
    // locks the center onto the axis from the start point (`points[0]`).
    orthoReference: "first",
    hasPreviewEntity: true,
  },
  "arc-sce-end-angle": {
    step: "arc-sce-end-angle",
    command: "ARC",
    category: "basic-2d",
    promptKey: "prompt.arc.sce_end_angle",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── ELLIPSE ─────────────────────────────────────────────────────────
  "ellipse-center": {
    step: "ellipse-center",
    command: "ELLIPSE",
    category: "basic-2d",
    promptKey: "prompt.ellipse.center",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "ellipse-major-end": {
    step: "ellipse-major-end",
    command: "ELLIPSE",
    category: "basic-2d",
    promptKey: "prompt.ellipse.major_end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "ellipse-minor-distance": {
    step: "ellipse-minor-distance",
    command: "ELLIPSE",
    category: "basic-2d",
    promptKey: "prompt.ellipse.minor_distance",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },

  // ── POLYLINE ────────────────────────────────────────────────────────
  "polyline-first-point": {
    step: "polyline-first-point",
    command: "PLINE",
    category: "polyline",
    promptKey: "prompt.polyline.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "polyline-next-point": {
    step: "polyline-next-point",
    command: "PLINE",
    category: "polyline",
    promptKey: "prompt.polyline.next",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "polyline-width-start": {
    step: "polyline-width-start",
    command: "PLINE",
    category: "polyline",
    promptKey: "prompt.polyline.width_start",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "polyline-width-end": {
    step: "polyline-width-end",
    command: "PLINE",
    category: "polyline",
    promptKey: "prompt.polyline.width_end",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── RECTANGLE ───────────────────────────────────────────────────────
  "rectangle-first-corner": {
    step: "rectangle-first-corner",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.rectangle.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rectangle-second-corner": {
    step: "rectangle-second-corner",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.rectangle.second",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner: `textInputReference: "first"` is only
    // for typed relative coords (`@dx,dy` from corner 1). ORTHO must NOT anchor
    // here: locking the diagonal corner onto corner 1's 0/90° axis collapses the
    // rectangle to zero height/width. AutoCAD never ortho-constrains a two-corner
    // box, so the ORTHO anchor is suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "rectangle-ready": {
    step: "rectangle-ready",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "rectangle-d-length": {
    step: "rectangle-d-length",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.rectangle.d_length",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rectangle-d-width": {
    step: "rectangle-d-width",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.rectangle.d_width",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rectangle-d-place": {
    step: "rectangle-d-place",
    command: "RECTANG",
    category: "basic-2d",
    promptKey: "prompt.rectangle.d_place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── POLYGON ─────────────────────────────────────────────────────────
  "polygon-sides-input": {
    step: "polygon-sides-input",
    command: "POLYGON",
    category: "basic-2d",
    promptKey: "prompt.polygon.sides",
    promptArgs: polygonSidesArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "polygon-mode-prompt": {
    step: "polygon-mode-prompt",
    command: "POLYGON",
    category: "basic-2d",
    promptKey: "prompt.polygon.mode",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "polygon-center": {
    step: "polygon-center",
    command: "POLYGON",
    category: "basic-2d",
    promptKey: "prompt.polygon.center",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "polygon-radius": {
    step: "polygon-radius",
    command: "POLYGON",
    category: "basic-2d",
    promptKey: "prompt.polygon.radius",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },

  // ── TEXT (single-line) ──────────────────────────────────────────────
  "text-insertion-point": {
    step: "text-insertion-point",
    command: "TEXT",
    category: "text",
    promptKey: "prompt.text.start",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "text-height": {
    step: "text-height",
    command: "TEXT",
    category: "text",
    promptKey: "prompt.text.height",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — on-screen SCALAR point prompt: a click here measures the text
    // height as the DISTANCE from the insertion point (`points[0]`) in
    // `advanceDispatch.ts`, so ORTHO must lock the drag onto the 0/90° axis of
    // that point. `textInputReference` is "none" (a typed height is an absolute
    // scalar, not a relative coord), so the ortho anchor DIVERGES → "first".
    orthoReference: "first",
    hasPreviewEntity: false,
  },
  "text-rotation": {
    step: "text-rotation",
    command: "TEXT",
    category: "text",
    promptKey: "prompt.text.rotation",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — on-screen ANGLE point prompt: a click here measures the text
    // rotation as the ANGLE from the insertion point (`points[0]`), so ORTHO
    // must lock the pick onto 0/90° from that point. Diverges from the "none"
    // typed reference → "first".
    orthoReference: "first",
    hasPreviewEntity: false,
  },
  "text-content": {
    step: "text-content",
    command: "TEXT",
    category: "text",
    promptKey: "prompt.text.content",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── MTEXT (multi-line) ──────────────────────────────────────────────
  "mtext-first-corner": {
    step: "mtext-first-corner",
    command: "MTEXT",
    category: "text",
    promptKey: "prompt.mtext.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mtext-second-corner": {
    step: "mtext-second-corner",
    command: "MTEXT",
    category: "text",
    promptKey: "prompt.mtext.second",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`): the
    // MTEXT text box is defined by two diagonal corners; ortho-locking the second
    // onto the first's axis collapses the box. ORTHO anchor suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "mtext-height": {
    step: "mtext-height",
    command: "MTEXT",
    category: "text",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mtext-content": {
    step: "mtext-content",
    command: "MTEXT",
    category: "text",
    promptKey: "prompt.mtext.content",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── BLOCK INSERT ────────────────────────────────────────────────────
  "block-name": {
    step: "block-name",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.name",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "block-insertion-point": {
    step: "block-insertion-point",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.insertion",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "block-scale": {
    step: "block-scale",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.scale",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "block-rotation": {
    step: "block-rotation",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.rotation",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "block-ready": {
    step: "block-ready",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── ARRAYRECT ───────────────────────────────────────────────────────
  "arrayrect-select": {
    step: "arrayrect-select",
    command: "ARRAYRECT",
    category: "array",
    promptKey: "prompt.arrayrect.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "arrayrect-base-point": {
    step: "arrayrect-base-point",
    command: "ARRAYRECT",
    category: "array",
    promptKey: "prompt.arrayrect.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "arrayrect-count": {
    step: "arrayrect-count",
    command: "ARRAYRECT",
    category: "array",
    promptKey: "prompt.arrayrect.count",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`): the
    // drag defines the grid's opposite corner (rows/cols auto-calc from the
    // 2D delta off `points[0]`). Ortho-locking the drag onto the base axis zeroes
    // one grid dimension (1×N / N×1), so the ORTHO anchor is suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "arrayrect-spacing": {
    step: "arrayrect-spacing",
    command: "ARRAYRECT",
    category: "array",
    promptKey: "prompt.arrayrect.spacing",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── ARRAYPOLAR ──────────────────────────────────────────────────────
  "arraypolar-select": {
    step: "arraypolar-select",
    command: "ARRAYPOLAR",
    category: "array",
    promptKey: "prompt.arraypolar.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "arraypolar-center": {
    step: "arraypolar-center",
    command: "ARRAYPOLAR",
    category: "array",
    promptKey: "prompt.arraypolar.center",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "arraypolar-count": {
    step: "arraypolar-count",
    command: "ARRAYPOLAR",
    category: "array",
    promptKey: "prompt.arraypolar.count",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "arraypolar-fill": {
    step: "arraypolar-fill",
    command: "ARRAYPOLAR",
    category: "array",
    promptKey: "prompt.arraypolar.fill",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── ARRAYPATH ───────────────────────────────────────────────────────
  "arraypath-select": {
    step: "arraypath-select",
    command: "ARRAYPATH",
    category: "array",
    promptKey: "prompt.arraypath.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "arraypath-path": {
    step: "arraypath-path",
    command: "ARRAYPATH",
    category: "array",
    promptKey: "prompt.arraypath.path",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "arraypath-method": {
    step: "arraypath-method",
    command: "ARRAYPATH",
    category: "array",
    promptKey: "prompt.arraypath.method",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "arraypath-params": {
    step: "arraypath-params",
    command: "ARRAYPATH",
    category: "array",
    promptKey: "prompt.arraypath.params",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── ARRAYEDIT ───────────────────────────────────────────────────────
  "arrayedit-select": {
    step: "arrayedit-select",
    command: "ARRAYEDIT",
    category: "array",
    promptKey: "prompt.arrayedit.select",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── HATCHEDIT ───────────────────────────────────────────────────────
  // Select a placed HATCH, then prompt pattern / scale / angle. Category
  // "hatch" (shared with the HATCH-create steps). The select step accepts a
  // point pick (click a hatch) OR a typed entity id; the three field prompts
  // are text / scalar inputs (no rubber-band reference point).
  "hatchedit-select": {
    step: "hatchedit-select",
    command: "HATCHEDIT",
    category: "hatch",
    promptKey: "prompt.hatchedit.select",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "hatchedit-pattern": {
    step: "hatchedit-pattern",
    command: "HATCHEDIT",
    category: "hatch",
    promptKey: "prompt.hatchedit.pattern",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "hatchedit-scale": {
    step: "hatchedit-scale",
    command: "HATCHEDIT",
    category: "hatch",
    promptKey: "prompt.hatchedit.scale",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "hatchedit-angle": {
    step: "hatchedit-angle",
    command: "HATCHEDIT",
    category: "hatch",
    promptKey: "prompt.hatchedit.angle",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── BLOCK shell flow (BLOCK / INSERT / BEDIT / BCLOSE / ATTDEF /
  //    DDATTE / EATTEDIT) ─────────────────────────────────────────────
  "block-select-objects": {
    step: "block-select-objects",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.select_objects",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "block-base-point": {
    step: "block-base-point",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.base_point",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "block-dialog": {
    step: "block-dialog",
    command: "BLOCK",
    category: "block-authoring",
    promptKey: "prompt.block.dialog",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "insert-dialog": {
    step: "insert-dialog",
    command: "INSERT",
    category: "block-authoring",
    promptKey: "prompt.insert.dialog",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "insert-specify-point": {
    step: "insert-specify-point",
    command: "INSERT",
    category: "block-authoring",
    promptKey: "prompt.insert.specify_point",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "insert-specify-scale": {
    step: "insert-specify-scale",
    command: "INSERT",
    category: "block-authoring",
    promptKey: "prompt.insert.specify_scale",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — on-screen SCALAR point prompt (same class as `text-height`):
    // INSERT "Specify scale on screen" measures the scale as the DISTANCE from
    // the insertion point (`points[0]`) in `advanceDispatch.ts`, so ORTHO must
    // lock the drag onto the 0/90° axis of that point. Diverges from "none".
    orthoReference: "first",
    hasPreviewEntity: false,
  },
  "insert-specify-rotation": {
    step: "insert-specify-rotation",
    command: "INSERT",
    category: "block-authoring",
    promptKey: "prompt.insert.specify_rotation",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — on-screen ANGLE point prompt: INSERT "Specify rotation on
    // screen" measures the angle from the insertion point (`points[0]`), so
    // ORTHO locks the pick onto 0/90° from that point. Diverges from "none".
    orthoReference: "first",
    hasPreviewEntity: false,
  },
  "insert-collect-attribs": {
    step: "insert-collect-attribs",
    command: "INSERT",
    category: "block-authoring",
    promptKey: "prompt.insert.attribute_value",
    promptArgs: insertCollectAttribsArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "bedit-prompt-name": {
    step: "bedit-prompt-name",
    command: "BEDIT",
    category: "block-authoring",
    promptKey: "prompt.bedit.enter_name",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "bedit-confirm-create": {
    step: "bedit-confirm-create",
    command: "BEDIT",
    category: "block-authoring",
    promptKey: "prompt.bedit.confirm_create",
    promptArgs: beditConfirmArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "bclose-confirm": {
    step: "bclose-confirm",
    command: "BCLOSE",
    category: "block-authoring",
    promptKey: "prompt.bclose.confirm",
    promptArgs: bcloseConfirmArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "attdef-dialog": {
    step: "attdef-dialog",
    command: "ATTDEF",
    category: "block-authoring",
    promptKey: "prompt.attdef.dialog_open",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "ddatte-select": {
    step: "ddatte-select",
    command: "DDATTE",
    category: "block-authoring",
    promptKey: "prompt.ddatte.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "ddatte-dialog": {
    step: "ddatte-dialog",
    command: "DDATTE",
    category: "block-authoring",
    promptKey: "prompt.ddatte.dialog_open",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "eattedit-select": {
    step: "eattedit-select",
    command: "EATTEDIT",
    category: "block-authoring",
    promptKey: "prompt.eattedit.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "eattedit-dialog": {
    step: "eattedit-dialog",
    command: "EATTEDIT",
    category: "block-authoring",
    promptKey: "prompt.eattedit.dialog_open",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── HATCH ───────────────────────────────────────────────────────────
  "hatch-pattern": {
    step: "hatch-pattern",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.pattern",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "hatch-first-corner": {
    step: "hatch-first-corner",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "hatch-second-corner": {
    step: "hatch-second-corner",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.second",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`): the
    // rectangular hatch boundary is two diagonal corners; ortho-locking the
    // second collapses the boundary. ORTHO anchor suppressed to null.
    orthoReference: "none",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "hatch-ready": {
    step: "hatch-ready",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.confirm",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "hatch-mode-prompt": {
    step: "hatch-mode-prompt",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.mode",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "hatch-pick-internal-point": {
    step: "hatch-pick-internal-point",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.pick_internal",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  "hatch-pick-additional": {
    step: "hatch-pick-additional",
    command: "HATCH",
    category: "hatch",
    promptKey: "prompt.hatch.pick_additional",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── DIMENSION (linear horizontal/vertical) ──────────────────────────
  "dimension-first-point": {
    step: "dimension-first-point",
    command: "DIMENSION",
    category: "dimension",
    promptKey: "prompt.dimension.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimension-second-point": {
    step: "dimension-second-point",
    command: "DIMENSION",
    category: "dimension",
    promptKey: "prompt.dimension.second",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "dimension-line-point": {
    step: "dimension-line-point",
    command: "DIMENSION",
    category: "dimension",
    promptKey: "prompt.dimension.line",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "dimension-ready": {
    step: "dimension-ready",
    command: "DIMENSION",
    category: "dimension",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── DIMALIGNED ──────────────────────────────────────────────────────
  "dimaligned-first-point": {
    step: "dimaligned-first-point",
    command: "DIMALIGNED",
    category: "dimension",
    promptKey: "prompt.dimaligned.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimaligned-second-point": {
    step: "dimaligned-second-point",
    command: "DIMALIGNED",
    category: "dimension",
    promptKey: "prompt.dimaligned.second",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "dimaligned-line-point": {
    step: "dimaligned-line-point",
    command: "DIMALIGNED",
    category: "dimension",
    promptKey: "prompt.dimaligned.line",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },

  // ── DIMANGULAR ──────────────────────────────────────────────────────
  "dimangular-first-line": {
    step: "dimangular-first-line",
    command: "DIMANGULAR",
    category: "dimension",
    promptKey: "prompt.dimangular.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimangular-second-line": {
    step: "dimangular-second-line",
    command: "DIMANGULAR",
    category: "dimension",
    promptKey: "prompt.dimangular.second",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "dimangular-arc-point": {
    step: "dimangular-arc-point",
    command: "DIMANGULAR",
    category: "dimension",
    promptKey: "prompt.dimangular.arc",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },

  // ── DIM3POINT ───────────────────────────────────────────────────────
  "dim3point-vertex": {
    step: "dim3point-vertex",
    command: "DIM3POINT",
    category: "dimension",
    promptKey: "prompt.dim3point.vertex",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dim3point-first-arm": {
    step: "dim3point-first-arm",
    command: "DIM3POINT",
    category: "dimension",
    promptKey: "prompt.dim3point.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dim3point-second-arm": {
    step: "dim3point-second-arm",
    command: "DIM3POINT",
    category: "dimension",
    promptKey: "prompt.dim3point.second",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dim3point-arc-point": {
    step: "dim3point-arc-point",
    command: "DIM3POINT",
    category: "dimension",
    promptKey: "prompt.dim3point.arc",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── DIMRADIUS / DIMDIAMETER ─────────────────────────────────────────
  "dimradius-select-arc": {
    step: "dimradius-select-arc",
    command: "DIMRADIUS",
    category: "dimension",
    promptKey: "prompt.dimradius.select",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimradius-dim-line": {
    step: "dimradius-dim-line",
    command: "DIMRADIUS",
    category: "dimension",
    promptKey: "prompt.dimradius.line",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "dimdiameter-select-arc": {
    step: "dimdiameter-select-arc",
    command: "DIMDIAMETER",
    category: "dimension",
    promptKey: "prompt.dimdiameter.select",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimdiameter-dim-line": {
    step: "dimdiameter-dim-line",
    command: "DIMDIAMETER",
    category: "dimension",
    promptKey: "prompt.dimdiameter.line",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },

  // ── DIMORDINATE ─────────────────────────────────────────────────────
  "dimordinate-feature-point": {
    step: "dimordinate-feature-point",
    command: "DIMORDINATE",
    category: "dimension",
    promptKey: "prompt.dimordinate.feature",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "dimordinate-leader-end": {
    step: "dimordinate-leader-end",
    command: "DIMORDINATE",
    category: "dimension",
    promptKey: "prompt.dimordinate.leader",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },

  // ── DIMBASELINE / DIMCONTINUE ───────────────────────────────────────
  "dimbaseline-second-point": {
    step: "dimbaseline-second-point",
    command: "DIMBASELINE",
    category: "dimension",
    promptKey: "prompt.dimbaseline.second",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },
  "dimcontinue-second-point": {
    step: "dimcontinue-second-point",
    command: "DIMCONTINUE",
    category: "dimension",
    promptKey: "prompt.dimcontinue.second",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },

  // ── MLEADER / MLEADEREDIT ───────────────────────────────────────────
  "mleader-arrow-point": {
    step: "mleader-arrow-point",
    command: "MLEADER",
    category: "mleader",
    promptKey: "prompt.mleader.arrow",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mleader-landing-point": {
    step: "mleader-landing-point",
    command: "MLEADER",
    category: "mleader",
    promptKey: "prompt.mleader.landing",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "mleader-content": {
    step: "mleader-content",
    command: "MLEADER",
    category: "mleader",
    promptKey: "prompt.mleader.content",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mleaderedit-select": {
    step: "mleaderedit-select",
    command: "MLEADEREDIT",
    category: "mleader",
    promptKey: "prompt.mleaderedit.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mleaderedit-options": {
    step: "mleaderedit-options",
    command: "MLEADEREDIT",
    category: "mleader",
    promptKey: "prompt.mleaderedit.options",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mleaderedit-add-leader-arrow": {
    step: "mleaderedit-add-leader-arrow",
    command: "MLEADEREDIT",
    category: "mleader",
    promptKey: "prompt.mleaderedit.add_arrow",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mleaderedit-remove-pick": {
    step: "mleaderedit-remove-pick",
    command: "MLEADEREDIT",
    category: "mleader",
    promptKey: "prompt.mleaderedit.remove_pick",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── REVCLOUD ────────────────────────────────────────────────────────
  "revcloud-first-corner": {
    step: "revcloud-first-corner",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "revcloud-second-corner": {
    step: "revcloud-second-corner",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.second",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`):
    // rectangular REVCLOUD is two diagonal corners; ortho-locking the second
    // collapses the cloud to zero area. ORTHO anchor suppressed to null. (The
    // polygonal/freehand REVCLOUD sub-modes are handled separately: polygonal is
    // a true `"last"` rubber-band, freehand is an explicit `"none"` trace.)
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "revcloud-ready": {
    step: "revcloud-ready",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },
  // REVCLOUD-MODES — Arc length / Style sub-prompts + Polygonal /
  // Freehand / Object creation-method flows.
  "revcloud-arc-length": {
    step: "revcloud-arc-length",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.arc_length",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "revcloud-style": {
    step: "revcloud-style",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.style",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "revcloud-polygonal-next": {
    step: "revcloud-polygonal-next",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.polygonal_next",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: true,
  },
  "revcloud-freehand-path": {
    step: "revcloud-freehand-path",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.freehand",
    acceptsPointInput: true,
    textInputReference: "last",
    // @codex P2 (round-4) — freehand REVCLOUD is a CONTINUOUS cursor trace, NOT
    // a discrete 0/90° pick: `handleStageHover` feeds each hover sample into
    // `advanceRevcloudFreehandHover`, which APPENDS a vertex (or auto-closes)
    // from the raw cursor travel. ORTHO must not axis-lock those samples (a
    // staircased cloud is not AutoCAD parity), and — critically — leaving this
    // as the "last" anchor makes `orthoAnchorKey` change on every hover-append,
    // which re-fires the anchor-change snap refresh (`Scene3DStage`) and replays
    // `onCanvasHover` at the same pointer, double-appending an extra stair-step
    // vertex per physical move. `orthoReference: "none"` → null anchor: the
    // freehand samples the raw cursor and the refresh never re-enters.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "revcloud-object-pick": {
    step: "revcloud-object-pick",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.object_pick",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "revcloud-object-reverse": {
    step: "revcloud-object-reverse",
    command: "REVCLOUD",
    category: "revcloud",
    promptKey: "prompt.revcloud.object_reverse",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── WIPEOUT ─────────────────────────────────────────────────────────
  "wipeout-first-corner": {
    step: "wipeout-first-corner",
    command: "WIPEOUT",
    category: "wipeout",
    promptKey: "prompt.wipeout.first_corner",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "wipeout-second-corner": {
    step: "wipeout-second-corner",
    command: "WIPEOUT",
    category: "wipeout",
    promptKey: "prompt.wipeout.second_corner",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`): the
    // WIPEOUT mask is two diagonal corners; ortho-locking the second collapses
    // the mask. ORTHO anchor suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "wipeout-ready": {
    step: "wipeout-ready",
    command: "WIPEOUT",
    category: "wipeout",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── SPLINE ──────────────────────────────────────────────────────────
  // Phase 2: spline-fit-next uses `resolvePromptKey` to switch between
  // `prompt.spline.fit_next` and `prompt.spline.fit_next_or_finish`
  // based on `session.points.length >= 2`.
  "spline-fit-first": {
    step: "spline-fit-first",
    command: "SPLINE",
    category: "spline",
    promptKey: "prompt.spline.fit_first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "spline-fit-next": {
    step: "spline-fit-next",
    command: "SPLINE",
    category: "spline",
    promptKey: "prompt.spline.fit_next",
    resolvePromptKey: splineFitNextPromptKey,
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — the fit-curve preview rubber-bands from the LAST fit
    // point to the hover, so ORTHO must lock every fit point after the
    // first onto the axis from `points.at(-1)`.
    orthoReference: "last",
    hasPreviewEntity: true,
  },

  // ── VIEWPORT (MVIEW) ────────────────────────────────────────────────
  "viewport-first-corner": {
    step: "viewport-first-corner",
    command: "MVIEW",
    category: "viewport",
    promptKey: "prompt.viewport.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "viewport-second-corner": {
    step: "viewport-second-corner",
    command: "MVIEW",
    category: "viewport",
    promptKey: "prompt.viewport.second",
    acceptsPointInput: true,
    textInputReference: "first",
    // @codex P2 — box/area OPPOSITE-corner (see `rectangle-second-corner`): the
    // MVIEW viewport rect is two diagonal corners; ortho-locking the second
    // collapses the viewport. ORTHO anchor suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "viewport-ready": {
    step: "viewport-ready",
    command: "MVIEW",
    category: "viewport",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: true,
  },

  // ── VPCLIP-INTERACTIVE ────────────────────────────────────────────
  "vpclip-select-viewport": {
    step: "vpclip-select-viewport",
    command: "VPCLIP",
    category: "viewport",
    promptKey: "prompt.vpclip.select_viewport",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "vpclip-first-corner": {
    step: "vpclip-first-corner",
    command: "VPCLIP",
    category: "viewport",
    promptKey: "prompt.vpclip.first_corner",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "vpclip-second-corner": {
    step: "vpclip-second-corner",
    command: "VPCLIP",
    category: "viewport",
    promptKey: "prompt.vpclip.second_corner",
    acceptsPointInput: true,
    textInputReference: "first",
    // box/area OPPOSITE-corner class (see `viewport-second-corner` above):
    // the clip rect is two diagonal corners — ortho-locking the second
    // would collapse it. ORTHO anchor suppressed to null.
    orthoReference: "none",
    hasPreviewEntity: true,
  },

  // ── MOVE / COPY / MIRROR ────────────────────────────────────────────
  "move-select": {
    step: "move-select",
    command: "MOVE",
    category: "modify",
    promptKey: "prompt.move.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "move-base-point": {
    step: "move-base-point",
    command: "MOVE",
    category: "modify",
    promptKey: "prompt.move.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "move-destination": {
    step: "move-destination",
    command: "MOVE",
    category: "modify",
    promptKey: "prompt.move.destination",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },
  "copy-select": {
    step: "copy-select",
    command: "COPY",
    category: "modify",
    promptKey: "prompt.copy.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "copy-base-point": {
    step: "copy-base-point",
    command: "COPY",
    category: "modify",
    // Phase 2: dominant base key + resolver for displacement-mode
    // override. The resolver mirrors the conditional in the legacy
    // `buildDraftPrompt` switch.
    promptKey: "prompt.copy.base_or_displacement",
    resolvePromptKey: copyBasePointPromptKey,
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "copy-destination": {
    step: "copy-destination",
    command: "COPY",
    category: "modify",
    promptKey: "prompt.copy.second_or_exit",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },
  "mirror-select": {
    step: "mirror-select",
    command: "MIRROR",
    category: "modify",
    promptKey: "prompt.mirror.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mirror-first-point": {
    step: "mirror-first-point",
    command: "MIRROR",
    category: "modify",
    promptKey: "prompt.mirror.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "mirror-second-point": {
    step: "mirror-second-point",
    command: "MIRROR",
    category: "modify",
    promptKey: "prompt.mirror.second",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },

  // ── ROTATE ──────────────────────────────────────────────────────────
  "rotate-select": {
    step: "rotate-select",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rotate-base-point": {
    step: "rotate-base-point",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rotate-angle": {
    step: "rotate-angle",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.angle",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },
  "rotate-ref-start": {
    step: "rotate-ref-start",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.ref_start",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "rotate-ref-end": {
    step: "rotate-ref-end",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.ref_end",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — the ROTATE Reference sub-flow stores [base, refStart] in
    // `points` at this step; a CLICK measures the reference angle from the
    // reference START (`points[1]`) in `draftModifyStateMachine.ts`, so ORTHO
    // must lock the reference-end pick onto the 0/90° axis of `refStart`.
    // `textInputReference` is "none", so the ortho anchor DIVERGES → "second"
    // (points[1] = refStart, mirroring the handler's exact index access — same
    // convention as the sibling `rotate-ref-new-angle` → "first" for points[0]).
    orthoReference: "second",
    hasPreviewEntity: false,
  },
  "rotate-ref-new-angle": {
    step: "rotate-ref-new-angle",
    command: "ROTATE",
    category: "modify",
    promptKey: "prompt.rotate.ref_new_angle",
    acceptsPointInput: false,
    textInputReference: "last",
    // @codex P2 (round-4) — a CLICK at this step measures its angle from the
    // rotation BASE (`points[0]`) in `draftModifyStateMachine.ts`, NOT from the
    // reference end (`points.at(-1)` = the `textInputReference` "last" anchor).
    // So ORTHO must lock the new-angle pick onto the 0/90° axis of the base
    // point; inheriting "last" would project the cursor around the reference end
    // and then measure the angle from the base, yielding a wrong rotation
    // whenever the reference end is not the base. `orthoReference: "first"` →
    // `points[0]` (base), matching the click semantics.
    orthoReference: "first",
    hasPreviewEntity: false,
  },

  // ── SCALE ───────────────────────────────────────────────────────────
  "scale-select": {
    step: "scale-select",
    command: "SCALE",
    category: "modify",
    promptKey: "prompt.scale.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "scale-base-point": {
    step: "scale-base-point",
    command: "SCALE",
    category: "modify",
    promptKey: "prompt.scale.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "scale-factor": {
    step: "scale-factor",
    command: "SCALE",
    category: "modify",
    promptKey: "prompt.scale.factor",
    acceptsPointInput: false,
    textInputReference: "last",
    hasPreviewEntity: false,
  },

  // ── OFFSET ──────────────────────────────────────────────────────────
  "offset-distance": {
    step: "offset-distance",
    command: "OFFSET",
    category: "modify",
    promptKey: "prompt.offset.distance",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "offset-select-entity": {
    step: "offset-select-entity",
    command: "OFFSET",
    category: "modify",
    promptKey: "prompt.offset.select",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "offset-side": {
    step: "offset-side",
    command: "OFFSET",
    category: "modify",
    promptKey: "prompt.offset.side",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── STRETCH ─────────────────────────────────────────────────────────
  "stretch-select-first": {
    step: "stretch-select-first",
    command: "STRETCH",
    category: "modify",
    promptKey: "prompt.stretch.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "stretch-select-second": {
    step: "stretch-select-second",
    command: "STRETCH",
    category: "modify",
    promptKey: "prompt.stretch.second",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "stretch-base": {
    step: "stretch-base",
    command: "STRETCH",
    category: "modify",
    promptKey: "prompt.stretch.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "stretch-destination": {
    step: "stretch-destination",
    command: "STRETCH",
    category: "modify",
    promptKey: "prompt.stretch.destination",
    acceptsPointInput: true,
    textInputReference: "last",
    hasPreviewEntity: false,
  },

  // ── TRIM / EXTEND ───────────────────────────────────────────────────
  "trim-select-edges": {
    step: "trim-select-edges",
    command: "TRIM",
    category: "modify",
    promptKey: "prompt.trim.select_edges",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "trim-pick-entity": {
    step: "trim-pick-entity",
    command: "TRIM",
    category: "modify",
    promptKey: "prompt.trim.pick_entity",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "extend-select-edges": {
    step: "extend-select-edges",
    command: "EXTEND",
    category: "modify",
    promptKey: "prompt.extend.select_edges",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "extend-pick-entity": {
    step: "extend-pick-entity",
    command: "EXTEND",
    category: "modify",
    promptKey: "prompt.extend.pick_entity",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── FILLET / CHAMFER ────────────────────────────────────────────────
  "fillet-radius": {
    step: "fillet-radius",
    command: "FILLET",
    category: "modify",
    promptKey: "prompt.fillet.radius",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "fillet-first-entity": {
    step: "fillet-first-entity",
    command: "FILLET",
    category: "modify",
    promptKey: "prompt.fillet.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "fillet-second-entity": {
    step: "fillet-second-entity",
    command: "FILLET",
    category: "modify",
    promptKey: "prompt.fillet.second",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "chamfer-distances": {
    step: "chamfer-distances",
    command: "CHAMFER",
    category: "modify",
    promptKey: "prompt.chamfer.distances",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "chamfer-first": {
    step: "chamfer-first",
    command: "CHAMFER",
    category: "modify",
    promptKey: "prompt.chamfer.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "chamfer-second": {
    step: "chamfer-second",
    command: "CHAMFER",
    category: "modify",
    promptKey: "prompt.chamfer.second",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── BREAK / JOIN / EXPLODE / LENGTHEN ───────────────────────────────
  "break-select": {
    step: "break-select",
    command: "BREAK",
    category: "modify",
    promptKey: "prompt.break.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "break-first-point": {
    step: "break-first-point",
    command: "BREAK",
    category: "modify",
    promptKey: "prompt.break.first",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "break-second-point": {
    step: "break-second-point",
    command: "BREAK",
    category: "modify",
    promptKey: "prompt.break.second",
    acceptsPointInput: true,
    textInputReference: "none",
    // @codex P2 — BREAK's second point is an ANCHORED pick of the same command:
    // `draftModifyStateMachine.ts` commits it as `point2` measured against the
    // first break point `point1 = session.points[0]`, so ORTHO must lock the
    // second break point onto the 0/90° axis of the first. `textInputReference`
    // is "none" (BREAK takes no typed relative coord here), so the ortho anchor
    // DIVERGES → "first" (points[0] = point1, the handler's exact index).
    orthoReference: "first",
    hasPreviewEntity: false,
  },
  "join-select": {
    step: "join-select",
    command: "JOIN",
    category: "modify",
    promptKey: "prompt.join.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "explode-select": {
    step: "explode-select",
    command: "EXPLODE",
    category: "modify",
    promptKey: "prompt.explode.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "lengthen-select": {
    step: "lengthen-select",
    command: "LENGTHEN",
    category: "modify",
    promptKey: "prompt.lengthen.select",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "lengthen-mode": {
    step: "lengthen-mode",
    command: "LENGTHEN",
    category: "modify",
    promptKey: "prompt.lengthen.mode",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "lengthen-value": {
    step: "lengthen-value",
    command: "LENGTHEN",
    category: "modify",
    promptKey: "prompt.lengthen.value",
    acceptsPointInput: false,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "lengthen-pick-end": {
    step: "lengthen-pick-end",
    command: "LENGTHEN",
    category: "modify",
    promptKey: "prompt.lengthen.pick_end",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── GEOM CONSTRAINT ─────────────────────────────────────────────────
  "geom-constraint-pick-first": {
    step: "geom-constraint-pick-first",
    command: "GCCONSTRAINT",
    category: "geom-constraint",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "geom-constraint-pick-second": {
    step: "geom-constraint-pick-second",
    command: "GCCONSTRAINT",
    category: "geom-constraint",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "geom-constraint-pick-more": {
    step: "geom-constraint-pick-more",
    command: "GCCONSTRAINT",
    category: "geom-constraint",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "geom-constraint-pick-reference-line": {
    step: "geom-constraint-pick-reference-line",
    command: "GCCONSTRAINT",
    category: "geom-constraint",
    promptKey: "prompt.idle", // TBD Phase 3 — dedicated key
    promptArgs: idleCommandArgs,
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },

  // ── BLOCK PARAMETER (BPARAM-*) ──────────────────────────────────────
  "param-linear-base": {
    step: "param-linear-base",
    command: "BPARAM-LINEAR",
    category: "block-param",
    promptKey: "prompt.bparam.linear.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-linear-end": {
    step: "param-linear-end",
    command: "BPARAM-LINEAR",
    category: "block-param",
    promptKey: "prompt.bparam.linear.end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "param-linear-label": {
    step: "param-linear-label",
    command: "BPARAM-LINEAR",
    category: "block-param",
    promptKey: "prompt.bparam.linear.label",
    acceptsPointInput: true,
    textInputReference: "second",
    // @codex P2 — FREE label/grip placement: this click stores the clicked
    // `point` VERBATIM as the parameter's `gripPosition` in
    // `draftStateMachine.blockAuthoring.ts` — it is NOT a rubber-band segment
    // from the parameter end. `textInputReference: "second"` is retained ONLY so
    // a typed relative coord (`@dx,dy` from the parameter end `points[1]`) still
    // works, but ORTHO must NOT axis-lock a free label position (AutoCAD lets
    // the label sit anywhere), so the ortho anchor is suppressed → "none". All
    // five BPARAM `*-label` steps share this contract.
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "param-point-place": {
    step: "param-point-place",
    command: "BPARAM-POINT",
    category: "block-param",
    promptKey: "prompt.bparam.point.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-basepoint-place": {
    step: "param-basepoint-place",
    command: "BPARAM-BASEPOINT",
    category: "block-param",
    promptKey: "prompt.bparam.basepoint.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-visibility-place": {
    step: "param-visibility-place",
    command: "BPARAM-VISIBILITY",
    category: "block-param",
    promptKey: "prompt.bparam.visibility.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-lookup-place": {
    step: "param-lookup-place",
    command: "BPARAM-LOOKUP",
    category: "block-param",
    promptKey: "prompt.bparam.lookup.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-flip-base": {
    step: "param-flip-base",
    command: "BPARAM-FLIP",
    category: "block-param",
    promptKey: "prompt.bparam.flip.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-flip-end": {
    step: "param-flip-end",
    command: "BPARAM-FLIP",
    category: "block-param",
    promptKey: "prompt.bparam.flip.end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "param-flip-label": {
    step: "param-flip-label",
    command: "BPARAM-FLIP",
    category: "block-param",
    promptKey: "prompt.bparam.flip.label",
    acceptsPointInput: true,
    textInputReference: "second",
    // @codex P2 — FREE label/grip placement (see `param-linear-label`): stored
    // verbatim as `gripPosition`; ORTHO anchor suppressed → "none".
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "param-polar-base": {
    step: "param-polar-base",
    command: "BPARAM-POLAR",
    category: "block-param",
    promptKey: "prompt.bparam.polar.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-polar-end": {
    step: "param-polar-end",
    command: "BPARAM-POLAR",
    category: "block-param",
    promptKey: "prompt.bparam.polar.end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "param-polar-label": {
    step: "param-polar-label",
    command: "BPARAM-POLAR",
    category: "block-param",
    promptKey: "prompt.bparam.polar.label",
    acceptsPointInput: true,
    textInputReference: "second",
    // @codex P2 — FREE label/grip placement (see `param-linear-label`): stored
    // verbatim as `gripPosition`; ORTHO anchor suppressed → "none".
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "param-xy-base": {
    step: "param-xy-base",
    command: "BPARAM-XY",
    category: "block-param",
    promptKey: "prompt.bparam.xy.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-xy-end": {
    step: "param-xy-end",
    command: "BPARAM-XY",
    category: "block-param",
    promptKey: "prompt.bparam.xy.end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "param-xy-label": {
    step: "param-xy-label",
    command: "BPARAM-XY",
    category: "block-param",
    promptKey: "prompt.bparam.xy.label",
    acceptsPointInput: true,
    textInputReference: "second",
    // @codex P2 — FREE label/grip placement (see `param-linear-label`): stored
    // verbatim as `gripPosition`; ORTHO anchor suppressed → "none".
    orthoReference: "none",
    hasPreviewEntity: true,
  },
  "param-rotation-base": {
    step: "param-rotation-base",
    command: "BPARAM-ROTATION",
    category: "block-param",
    promptKey: "prompt.bparam.rotation.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-rotation-radius-end": {
    step: "param-rotation-radius-end",
    command: "BPARAM-ROTATION",
    category: "block-param",
    promptKey: "prompt.bparam.rotation.radius_end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },
  "param-rotation-label": {
    step: "param-rotation-label",
    command: "BPARAM-ROTATION",
    category: "block-param",
    promptKey: "prompt.bparam.rotation.label",
    acceptsPointInput: true,
    textInputReference: "second",
    // @codex P2 — FREE label/grip placement (see `param-linear-label`): stored
    // verbatim as `gripPosition`; ORTHO anchor suppressed → "none".
    orthoReference: "none",
    hasPreviewEntity: false,
  },
  "param-alignment-base": {
    step: "param-alignment-base",
    command: "BPARAM-ALIGNMENT",
    category: "block-param",
    promptKey: "prompt.bparam.alignment.base",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "param-alignment-end": {
    step: "param-alignment-end",
    command: "BPARAM-ALIGNMENT",
    category: "block-param",
    promptKey: "prompt.bparam.alignment.end",
    acceptsPointInput: true,
    textInputReference: "first",
    hasPreviewEntity: true,
  },

  // ── BLOCK ACTION (BACTION-*) ────────────────────────────────────────
  "action-move-pick-parameter": {
    step: "action-move-pick-parameter",
    command: "BACTION-MOVE",
    category: "block-action",
    promptKey: "prompt.baction.move.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-move-select-objects": {
    step: "action-move-select-objects",
    command: "BACTION-MOVE",
    category: "block-action",
    promptKey: "prompt.baction.move.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-move-place": {
    step: "action-move-place",
    command: "BACTION-MOVE",
    category: "block-action",
    promptKey: "prompt.baction.move.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-scale-pick-parameter": {
    step: "action-scale-pick-parameter",
    command: "BACTION-SCALE",
    category: "block-action",
    promptKey: "prompt.baction.scale.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-scale-select-objects": {
    step: "action-scale-select-objects",
    command: "BACTION-SCALE",
    category: "block-action",
    promptKey: "prompt.baction.scale.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-scale-place": {
    step: "action-scale-place",
    command: "BACTION-SCALE",
    category: "block-action",
    promptKey: "prompt.baction.scale.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-stretch-pick-parameter": {
    step: "action-stretch-pick-parameter",
    command: "BACTION-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.stretch.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-stretch-select-objects": {
    step: "action-stretch-select-objects",
    command: "BACTION-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.stretch.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-stretch-frame-corner-1": {
    step: "action-stretch-frame-corner-1",
    command: "BACTION-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.stretch.frame_corner_1",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-stretch-frame-corner-2": {
    step: "action-stretch-frame-corner-2",
    command: "BACTION-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.stretch.frame_corner_2",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-stretch-place": {
    step: "action-stretch-place",
    command: "BACTION-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.stretch.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-polar-stretch-pick-parameter": {
    step: "action-polar-stretch-pick-parameter",
    command: "BACTION-POLAR-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.polar_stretch.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-polar-stretch-select-objects": {
    step: "action-polar-stretch-select-objects",
    command: "BACTION-POLAR-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.polar_stretch.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-polar-stretch-frame-corner-1": {
    step: "action-polar-stretch-frame-corner-1",
    command: "BACTION-POLAR-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.polar_stretch.frame_corner_1",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-polar-stretch-frame-corner-2": {
    step: "action-polar-stretch-frame-corner-2",
    command: "BACTION-POLAR-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.polar_stretch.frame_corner_2",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-polar-stretch-place": {
    step: "action-polar-stretch-place",
    command: "BACTION-POLAR-STRETCH",
    category: "block-action",
    promptKey: "prompt.baction.polar_stretch.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-rotation-pick-parameter": {
    step: "action-rotation-pick-parameter",
    command: "BACTION-ROTATION",
    category: "block-action",
    promptKey: "prompt.baction.rotation.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-rotation-select-objects": {
    step: "action-rotation-select-objects",
    command: "BACTION-ROTATION",
    category: "block-action",
    promptKey: "prompt.baction.rotation.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-rotation-place": {
    step: "action-rotation-place",
    command: "BACTION-ROTATION",
    category: "block-action",
    promptKey: "prompt.baction.rotation.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-flip-pick-parameter": {
    step: "action-flip-pick-parameter",
    command: "BACTION-FLIP",
    category: "block-action",
    promptKey: "prompt.baction.flip.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-flip-select-objects": {
    step: "action-flip-select-objects",
    command: "BACTION-FLIP",
    category: "block-action",
    promptKey: "prompt.baction.flip.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-flip-place": {
    step: "action-flip-place",
    command: "BACTION-FLIP",
    category: "block-action",
    promptKey: "prompt.baction.flip.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-array-pick-parameter": {
    step: "action-array-pick-parameter",
    command: "BACTION-ARRAY",
    category: "block-action",
    promptKey: "prompt.baction.array.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-array-select-objects": {
    step: "action-array-select-objects",
    command: "BACTION-ARRAY",
    category: "block-action",
    promptKey: "prompt.baction.array.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-array-place": {
    step: "action-array-place",
    command: "BACTION-ARRAY",
    category: "block-action",
    promptKey: "prompt.baction.array.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-lookup-pick-parameter": {
    step: "action-lookup-pick-parameter",
    command: "BACTION-LOOKUP",
    category: "block-action",
    promptKey: "prompt.baction.lookup.pick_parameter",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-lookup-select-objects": {
    step: "action-lookup-select-objects",
    command: "BACTION-LOOKUP",
    category: "block-action",
    promptKey: "prompt.baction.lookup.select_objects",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
  "action-lookup-place": {
    step: "action-lookup-place",
    command: "BACTION-LOOKUP",
    category: "block-action",
    promptKey: "prompt.baction.lookup.place",
    acceptsPointInput: true,
    textInputReference: "none",
    hasPreviewEntity: false,
  },
};

/**
 * Type-safe accessor. Equivalent to `DRAFT_STEP_REGISTRY[step]` but
 * provides a single named entry-point for future tracing / metrics.
 */
export function getDraftStepDescriptor(step: DraftStep): DraftStepDescriptor {
  return DRAFT_STEP_REGISTRY[step];
}

/**
 * Frozen list of all registered DraftSteps. Useful for tests and
 * future iteration over all steps.
 */
export const DRAFT_STEP_LIST: ReadonlyArray<DraftStep> = Object.freeze(
  Object.keys(DRAFT_STEP_REGISTRY) as DraftStep[],
);
